import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Loader2, LogIn, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { PasswordInput } from "@/components/PasswordInput";
import { digits, errMsg, maskDoc } from "@/lib/api";
import { boyApi, boyToken } from "@/lib/boy";
import { cn } from "@/lib/utils";

function ModeSwitch({ mode, setMode }) {
  const opt = (k, label, Icon) => (
    <button type="button" onClick={() => setMode(k)} data-testid={`boy-login-mode-${k}`}
      className={cn("flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors", mode === k ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground")}>
      <Icon className="h-4 w-4" />{label}
    </button>
  );
  return <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">{opt("senha", "Senha", KeyRound)}{opt("codigo", "Código no WhatsApp", MessageCircle)}</div>;
}

export function MerchantLogin({ code, info, onLogged }) {
  const bx = useMemo(() => boyApi(code), [code]);
  const [mode, setMode] = useState("senha");
  const [doc, setDoc] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const done = (data) => {
    boyToken.set(code, data.token);
    onLogged();
  };
  const login = (e) => {
    e.preventDefault();
    run(async () => done((await bx.post("/login", { document: digits(doc), password })).data));
  };
  const sendCode = () => run(async () => {
    const { data } = await bx.post("/login/code", { document: digits(doc) });
    setSentTo(data.sent_to);
    toast.success(`Código enviado para o WhatsApp ${data.sent_to}`);
  });
  const verify = (e) => {
    e.preventDefault();
    run(async () => done((await bx.post("/login/code/verify", { document: digits(doc), code: otp })).data));
  };
  const spin = <Loader2 className="mr-2 h-4 w-4 animate-spin" />;
  return (
    <div className="card-surface space-y-5 p-6" data-testid="boy-login-card">
      <div><h1 className="text-2xl font-bold">Área do lojista</h1><p className="text-sm text-muted-foreground">Entre para pedir entregas com os motoboys da {info.store}.</p></div>
      {info.code_login && <ModeSwitch mode={mode} setMode={(m) => { setMode(m); setSentTo(""); }} />}
      <Field label="CPF ou CNPJ"><Input value={doc} onChange={(e) => setDoc(maskDoc(e.target.value))} inputMode="numeric" autoComplete="username" data-testid="boy-login-document" /></Field>
      {mode === "senha" ? (
        <form onSubmit={login} className="space-y-4">
          <Field label="Senha"><PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" testId="boy-login-password" /></Field>
          <Button type="submit" className="h-11 w-full" disabled={busy} data-testid="boy-login-submit">{busy ? spin : <LogIn className="mr-2 h-4 w-4" />}Entrar</Button>
        </form>
      ) : !sentTo ? (
        <Button className="h-11 w-full" onClick={sendCode} disabled={busy || digits(doc).length < 11} data-testid="boy-login-send-code">{busy ? spin : <MessageCircle className="mr-2 h-4 w-4" />}Enviar código no WhatsApp</Button>
      ) : (
        <form onSubmit={verify} className="space-y-4">
          <Field label={`Código enviado para ${sentTo}`}>
            <Input value={otp} onChange={(e) => setOtp(digits(e.target.value).slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" className="text-center font-mono text-2xl tracking-[0.4em]" data-testid="boy-login-code-input" />
          </Field>
          <Button type="submit" className="h-11 w-full" disabled={busy || otp.length !== 6} data-testid="boy-login-verify">{busy ? spin : <LogIn className="mr-2 h-4 w-4" />}Entrar</Button>
          <button type="button" onClick={sendCode} disabled={busy} className="w-full text-center text-sm text-primary hover:underline" data-testid="boy-login-resend">Reenviar código</button>
        </form>
      )}
      <p className="text-center text-sm text-muted-foreground">Ainda não tem cadastro? <Link to={`/boy/${code}/lojista`} className="font-medium text-primary hover:underline" data-testid="boy-login-signup-link">Cadastre sua empresa</Link></p>
    </div>
  );
}
